import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import * as Yup from "yup";

import { getSettings, updateCustomizations, updateSettings } from "src/api/settings";
import { useAuth } from "src/providers/AuthProvider";
import withPermissionWrapper from "src/hooks/withPermissionWrapper";
import { TextField } from "src/components/form";
import { Button } from "src/components/ui/button";
import { Checkbox } from "src/components/ui/checkbox";
import { SettingsPanel } from "src/components/common";
import { getCustomizationValue, showSuccessToast } from "src/lib/utils";
import type { BusinessCustomization } from "src/types/auth";

const validationSchema = Yup.object().shape({
	businessUuid: Yup.string().required(),
	business_name: Yup.string().required("This field is required"),
	business_address: Yup.string().required("This field is required"),
	vat: Yup.number().min(0, "Minimum amount is zero").max(100, "Maximum amount is 100").required("This field is required"),
	is_vat_enabled: Yup.boolean(),
	is_transaction_fee_enabled: Yup.boolean(),
});

const customizationValidationSchema = Yup.object().shape({
	businessUuid: Yup.string().required(),
	customizations: Yup.array().of(
		Yup.object().shape({
			label: Yup.string().required(),
			value: Yup.string().required("This field is required"),
		})
	),
});

/** Order matches the `business_customization` array the API returns. */
const customizationFields = [
	{ index: 0, label: "Customer Label" },
	{ index: 1, label: "Sub-Account Label" },
	{ index: 2, label: "Invoice Label" },
	{ index: 3, label: "Inventory Label" },
	{ index: 4, label: "Invoice Group Label" },
];

const Settings = () => {
	const { user } = useAuth();
	const queryClient = useQueryClient();

	const { data } = useQuery({
		queryKey: ["settings"],
		queryFn: () => getSettings({ businessUuid: user?.business_uuid }),
		suspense: true,
	});

	const settings = data?.data;
	const customizations: BusinessCustomization[] = user?.business_customization ?? [];

	const { mutate, isLoading } = useMutation(updateSettings, {
		onSuccess: ({ message }) => {
			showSuccessToast(message || "Settings updated");
			queryClient.invalidateQueries(["settings"]);
		},
	});

	const { mutate: customizationMutate, isLoading: isCustomizationLoading } = useMutation(updateCustomizations, {
		onSuccess: ({ message }) => {
			showSuccessToast(message || "Labels updated");
			queryClient.invalidateQueries(["user"]);
		},
	});

	return (
		<div className="tw-space-y-4">
			<Formik
				initialValues={{
					businessUuid: user?.business_uuid,
					business_name: user?.business_name || "",
					business_address: user?.business_address || "",
					vat: settings?.vat ?? 2,
					is_vat_enabled: settings?.is_vat_enabled ?? true,
					is_transaction_fee_enabled: settings?.is_transaction_fee_enabled ?? true,
					customizations,
				}}
				validationSchema={validationSchema}
				onSubmit={(values) => mutate(values)}
			>
				{({ values, handleChange, handleSubmit, setFieldValue, errors, touched }) => (
					<form onSubmit={handleSubmit}>
						<SettingsPanel
							title="Brand & Business"
							description="Your business identity, VAT and transaction fee settings."
							actions={
								<Button size="sm" type="submit" disabled={isLoading} className="tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
									{isLoading ? "Saving..." : "Update Settings"}
								</Button>
							}
						>
							<div className="tw-grid tw-gap-4">
								<TextField
									label="Business name"
									name="business_name"
									placeholder="Business name"
									value={values.business_name}
									error={touched.business_name && errors.business_name}
									onChange={handleChange}
									required
								/>
								<TextField
									label="Business address"
									name="business_address"
									placeholder="Business address"
									value={values.business_address}
									error={touched.business_address && errors.business_address}
									onChange={handleChange}
									required
								/>

								<div className="tw-rounded-[10px] tw-border tw-border-border tw-p-3.5">
									<label htmlFor="is_vat_enabled" className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2.5">
										<Checkbox
											id="is_vat_enabled"
											checked={values.is_vat_enabled}
											onCheckedChange={(checked) => {
												// Turning VAT off zeroes the rate, so a disabled charge leaves no stale percentage behind.
												if (!checked) setFieldValue("vat", 0);
												setFieldValue("is_vat_enabled", checked === true);
											}}
										/>
										<span className="tw-text-[13.5px] tw-text-text">Does your business charge VAT?</span>
									</label>

									{values.is_vat_enabled && (
										<TextField
											label="VAT (%)"
											name="vat"
											type="number"
											min={0}
											max={100}
											placeholder="0"
											value={values.vat}
											error={touched.vat && errors.vat}
											onChange={handleChange}
											containerClassName="tw-mt-3"
										/>
									)}
								</div>

								<div className="tw-rounded-[10px] tw-border tw-border-border tw-p-3.5">
									<label htmlFor="is_transaction_fee_enabled" className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2.5">
										<Checkbox
											id="is_transaction_fee_enabled"
											checked={values.is_transaction_fee_enabled}
											onCheckedChange={(checked) => setFieldValue("is_transaction_fee_enabled", checked === true)}
										/>
										<span className="tw-text-[13.5px] tw-text-text">
											{`Charge ${getCustomizationValue(user, "customer_label", true) || "customer"} transaction fee?`}
										</span>
									</label>
								</div>
							</div>
						</SettingsPanel>
					</form>
				)}
			</Formik>

			<Formik
				initialValues={{ businessUuid: user?.business_uuid, customizations }}
				validationSchema={customizationValidationSchema}
				enableReinitialize
				onSubmit={(values) => customizationMutate(values)}
			>
				{({ values, handleChange, handleSubmit }) => (
					<form onSubmit={handleSubmit}>
						<SettingsPanel
							title="Labels"
							description="Rename modules to match your business. Use the singular form (e.g. Customer, not Customers)."
							actions={
								<Button
									size="sm"
									type="submit"
									disabled={isCustomizationLoading}
									className="tw-bg-teal tw-text-white hover:tw-bg-teal-dark"
								>
									{isCustomizationLoading ? "Saving..." : "Update Labels"}
								</Button>
							}
						>
							<div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
								{customizationFields.map(({ index, label }) =>
									values.customizations?.[index] ? (
										<TextField
											key={label}
											label={label}
											name={`customizations.${index}.value`}
											placeholder={label}
											value={values.customizations[index].value}
											onChange={handleChange}
										/>
									) : null
								)}
							</div>
						</SettingsPanel>
					</form>
				)}
			</Formik>
		</div>
	);
};

export default withPermissionWrapper(Settings, "update_business_settings");
